<template>
  <v-container fluid class="pa-7">
    <header-component class="pb-4" :title="$vuetify.lang.t('$vuetify.orders.detail', order && order.id)">
    </header-component>

    <v-row>
      <v-col cols="12">
        <v-row v-if="order">
          <v-col cols="12" lg="8">
            <v-row>
              <v-col cols="12">
                <OrderStateCardComponent></OrderStateCardComponent>

                <v-card class="pt-1 pb-0 px-3">
                  <v-row class="h-separator">
                    <v-col cols="12">
                      <OrderItemsListComponent :edittingDisabled="edittingDisabled" />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" class="pb-6">
                      <v-row>
                        <v-col cols="7" class="py-0">
                          <div>{{ $vuetify.lang.t(`$vuetify.labels.subtotal`) }}</div>
                        </v-col>
                        <v-col v-if="order" cols="5" class="d-flex flex-column align-end justify-end py-0">
                          {{ order.taxedItemsPrice.formatted }}
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="7" class="py-0">
                          <div>{{ $vuetify.lang.t(`$vuetify.labels.shipping`) }}</div>
                        </v-col>
                        <v-col
                          cols="5"
                          class="d-flex flex-column align-end justify-end py-0"
                          v-if="order.shipping"
                        >
                          <div>{{ order.shipping.original_prices.taxed_price.formatted }}</div>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="7" class="py-0">
                          <div v-if="formLanguage">
                            {{
                              order.paymentMethods && order.paymentMethods[0].name
                                ? order.paymentMethods[0].name[formLanguage]
                                : '-'
                            }}
                          </div>
                        </v-col>
                        <v-col cols="5"
                               class="d-flex flex-column align-end justify-end py-0"
                               v-if="order.payment"
                        >
                          <div>
                            {{ order.payment.original_prices.taxed_price.formatted }}
                          </div>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="7" class="py-0">
                          <div class="d-flex" v-for="discount in discountsForPurchase" :key="discount.id">
                            <div class="d-flex align-center">
                              {{ discount.name | multilingual(formLanguage) }}
                              {{ $vuetify.lang.t(`$vuetify.labels.wat`) }}
                              {{ discount.taxRate.formatted }}
                              ({{ discount.code }})
                            </div>
                          </div>
                        </v-col>
                        <v-col cols="5" class="d-flex flex-column align-end justify-end py-0">
                          <div v-for="discount in discountsForPurchase" :key="discount.id">
                            {{ discount.discountedTaxedAmount.formatted }}
                          </div>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="7" class="py-0">
                          <div class="d-flex" v-for="account in customerAccounts" :key="account.id">
                            <div class="d-flex align-center">
                              {{ account.name | multilingual(formLanguage) }}
                              {{ $vuetify.lang.t(`$vuetify.labels.wat`) }}
                              {{ account.taxRate.formatted }}
                              {{ $vuetify.lang.t('$vuetify.labels.creditAmount', account.amount) }}
                            </div>
                          </div>
                        </v-col>
                        <v-col cols="5" class="d-flex flex-column align-end justify-end py-0">
                          <div v-for="account in customerAccounts" :key="account.id">
                            {{ account.convertedTaxedAmount.formatted }}
                          </div>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="7" class="py-0">
                          <div class="mt-4" :class="{ 'font-weight-bold': !itemsToRemove.length }">
                            {{ $vuetify.lang.t(`$vuetify.labels.total`) }}
                          </div>
                        </v-col>
                        <v-col
                          v-if="order.shippingMethods"
                          cols="5"
                          class="d-flex flex-column align-end justify-end py-0"
                        >
                          <div class="mt-4" :class="{ 'font-weight-bold': !itemsToRemove.length }">
                            {{ order.taxedPrice.formatted }}
                          </div>
                        </v-col>
                      </v-row>

                      <v-row>
                        <v-col cols="7" class="py-0">
                          <div>{{ $vuetify.lang.t(`$vuetify.labels.wat`) }}</div>
                        </v-col>
                        <v-col cols="5" class="d-flex flex-column align-end justify-end py-0">
                          <div>{{ order.taxAmount.formatted }}</div>
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                  <v-btn
                    v-if="!edittingDisabled"
                    color="primary"
                    class="mt-3 clear-changes-btn"
                    @click="clearItemsToRemove"
                    :disabled="!itemsToRemove.length"
                  >
                    <span v-if="orderItemsRequestStatus !== REQUEST_STATUS.LOADING">
                      {{ $vuetify.lang.t('$vuetify.labels.resetChanges') }}
                    </span>
                    <div v-else class="d-flex align-center justify-center h-100">
                      <v-progress-circular :size="25" color="white" indeterminate :width="3" />
                    </div>
                  </v-btn>
                </v-card>
              </v-col>
            </v-row>

            <v-row :class="{ 'mt-6': edittingDisabled }">
              <v-col cols="12" class="py-0">
                <v-card class="py-3 px-3 mb-3">
                  <h4 class="text-uppercase mb-3">
                    {{ $vuetify.lang.t(`$vuetify.labels.customerNote`) }}
                  </h4>
                  <p v-if="order.customerNote">{{ order.customerNote }}</p>
                  <p v-else>{{ $vuetify.lang.t(`$vuetify.labels.noNote`) }}</p>
                </v-card>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" class="py-0 mb-3">
                <ShipmentCardComponent />
              </v-col>

              <v-col cols="12" class="py-0">
                <PaymentCardComponent />
              </v-col>
            </v-row>
          </v-col>

          <v-col cols="12" lg="4">
            <CustomerCardComponent :customerImage="customerImage"></CustomerCardComponent>

            <AddressCardComponent
              v-for="address in order.addresses"
              :key="address.type"
              :address="address"
            ></AddressCardComponent>

            <v-card class="py-3 px-3 mb-5">
              <div>
                <h4 class="text-uppercase mb-3">{{ $vuetify.lang.t('$vuetify.labels.info') }}</h4>
                <table v-if="order">
                  <tr>
                    <td class="font-weight-bold">
                      {{ $vuetify.lang.t(`$vuetify.labels.orderDate`) }}
                    </td>
                    <td class="pl-3">{{ formatDate(order.createdAt) }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold">
                      {{ $vuetify.lang.t(`$vuetify.labels.paidAt`) }}
                    </td>
                    <td class="pl-3">
                      {{ order.paidAt ? formatDate(order.paidAt) : $vuetify.lang.t(`$vuetify.labels.notPaidYet`) }}
                    </td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold">
                      {{ $vuetify.lang.t(`$vuetify.labels.typeOfOrder`) }}
                    </td>
                    <td class="pl-3">Z e-shopu</td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold">
                      {{ $vuetify.lang.t(`$vuetify.labels.weight`) }}
                    </td>
                    <td class="pl-3">{{ order.weight.value }}{{ order.weight.unit }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold">{{ $vuetify.lang.t(`$vuetify.labels.ip`) }}</td>
                    <td class="pl-3">{{ order.clientIp }}</td>
                  </tr>
                </table>
              </div>
            </v-card>
          </v-col>
        </v-row>
        <v-row v-else>
          <v-col cols="12">
            <div class="d-flex align-center justify-center">
              <v-progress-circular :size="35" color="primary" indeterminate :width="3" />
            </div>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <footer-component v-if="!edittingDisabled">
      <template v-slot:actions>
        <div class="d-flex footer-buttons">
          <CancelOrderComponent
            class="mr-3"
            :show="showCancelModal"
            :disabled="orderItemsRequestStatus === REQUEST_STATUS.LOADING || calculatorRequestPending"
            @close="showCancelModal = false"
          />
          <v-spacer></v-spacer>
          <v-btn
            class="mr-3"
            color="primary"
            @click="handleShowConfirmModal"
            :disabled="orderItemsRequestStatus === REQUEST_STATUS.LOADING || calculatorRequestPending"
          >
            <span v-if="orderItemsRequestStatus !== REQUEST_STATUS.LOADING && !calculatorRequestPending">
              {{ $vuetify.lang.t('$vuetify.labels.saveChanges') }}
            </span>
            <div v-else class="d-flex align-center justify-center h-100">
              <v-progress-circular :size="25" color="white" indeterminate :width="3" />
            </div>
          </v-btn>
        </div>
      </template>
    </footer-component>

    <DialogComponent
      :header="`${$vuetify.lang.t('$vuetify.orders.confirmChanges')}?`"
      :text="confirmModalText"
      :show="showConfirmModal"
      @close-modal="showConfirmModal = false"
    >
      <template slot="actions">
        <v-btn color="error" @click="showConfirmModal = false">{{ $vuetify.lang.t('$vuetify.basic.no') }}</v-btn>
        <v-spacer></v-spacer>
        <v-btn color="primary" @click="updateOrderItems">{{ $vuetify.lang.t('$vuetify.labels.takeProfit') }}</v-btn>
      </template>
    </DialogComponent>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { formatDateTime } from '@/common/helpers/date';
import OrderItemsTransformer from '../../common/helpers/orderItemsTransformer';
import HeaderComponent from '../../components/layout/HeaderComponent.vue';
import FooterComponent from '../../components/layout/FooterComponent.vue';
import OrderItemsListComponent from '../../components/orders/OrderItemsListComponent.vue';
import CustomerCardComponent from '../../components/orders/customer-card/CustomerCardComponent.vue';
import AddressCardComponent from '../../components/orders/address-card/AddressCardComponent.vue';
import ShipmentCardComponent from '../../components/orders/shipment-card/ShipmentCardComponent.vue';
import PaymentCardComponent from '../../components/orders/payment-card/PaymentCardComponent.vue';
import OrderStateCardComponent from '../../components/orders/order-state-card/OrderStateCardComponent.vue';
import DialogComponent from '../../components/global/DialogComponent.vue';
import CancelOrderComponent from '../../components/orders/CancelOrderComponent.vue';
import REQUEST_STATUS from '../../common/enums/requestStatus';
import ORDER_STATE from '../../common/enums/orderState';

export default {
  components: {
    HeaderComponent,
    FooterComponent,
    OrderItemsListComponent,
    CustomerCardComponent,
    ShipmentCardComponent,
    PaymentCardComponent,
    AddressCardComponent,
    DialogComponent,
    CancelOrderComponent,
    OrderStateCardComponent,
  },

  data() {
    return {
      REQUEST_STATUS,
      ORDER_STATE,
      customerImage: null,
      showConfirmModal: false,
      showCancelModal: false,
      calculatorRequestPending: false,
      orderSum: null,
    };
  },

  computed: {
    ...mapGetters({
      status: 'orders/getStatus',
      getOrder: 'orders/getOrder',
      itemsToRemove: 'orderDetail/getItemsToRemove',
      orderItemsRequestStatus: 'orderDetail/getStatus',
      orderStates: 'orderStates/getAllOrderStates',
      formLanguage: 'languages/formLanguage',
      getCurrencyById: 'currencies/getCurrency',
    }),

    orderId() {
      return Number(this.$route.params.id);
    },

    order() {
      return this.getOrder(this.orderId);
    },

    allItemsRemoved() {
      if (!this.order || !this.order.items) {
        return false;
      }

      return !this.order.items.filter(orderItem => {
        return !this.itemsToRemove.includes(orderItem);
      }).length;
    },

    edittingDisabled() {
      if (!this.order) {
        return false;
      }

      return (
        this.order.orderStateId === ORDER_STATE.CANCELLED ||
        this.order.orderStateId === ORDER_STATE.CARRIER_PAYMENT_COMPLETED
      );
    },

    discountsForPurchase() {
      return this.order && this.order.discounts ? this.order.discounts.filter(curr => curr.isForPurchase) : [];
    },

    customerAccounts() {
      if (!this.order) {
        return [];
      }

      return this.order.customerAccounts;
    },

    confirmModalText() {
      if (!this.order || !this.order.items || !this.itemsToRemove) {
        return '';
      }

      return this.$vuetify.lang.t('$vuetify.messages.confirmOrderChange');
    },
  },

  watch: {
    orderId() {
      this.fetchOrder(this.orderId).catch(this._redirectToList);
    },

    itemsToRemove: {
      deep: true,
      handler(val) {
        if (this.allItemsRemoved) {
          return;
        }

        if (!val || !val.length) {
          return;
        }

        this._calculateNewPrice();
      },
    },
  },

  methods: {
    ...mapActions({
      fetchOrder: 'orders/fetchOrder',
      setLastChange: 'orders/setLastChange',
      handleError: 'orders/handleError',
      updateItemsToRemove: 'orderDetail/updateItemsToRemove',
      postOrderItems: 'orderDetail/postOrderItems',
      fetchOrderStates: 'orderStates/fetchAllOrderStates',
    }),

    updateOrderItems() {
      this.showConfirmModal = false;

      this.postOrderItems(this.orderId)
        .then(order => {
          const lastOrderChange = {
            orderId: this.orderId,
            itemsCount: this.order.items.length - this.itemsToRemove.length,
            totalTaxedPrice: order.taxedPrice.formatted,
          };
          this.setLastChange(lastOrderChange);
          this._redirectToList();
          window.scrollTo(0, 0);
        })
        .catch(() => {});
    },

    clearItemsToRemove() {
      this.updateItemsToRemove([]);
    },

    formatDate(date) {
      return formatDateTime(date);
    },

    handleShowConfirmModal() {
      if (this.allItemsRemoved) {
        this.showCancelModal = true;
        return;
      }

      this._calculateNewPrice().then(() => {
        this.showConfirmModal = true;
      });
    },

    _calculateNewPrice() {
      const itemsToRemoveIds = this.itemsToRemove.map(itemToRemove => itemToRemove.id);
      OrderItemsTransformer.transformForOrderItemsUpdate(this.order, itemsToRemoveIds);

      return Promise.resolve();
    },

    _redirectToList() {
      this.$router.push({ name: 'orders' });
    },
  },

  created() {
    this.fetchOrderStates();
    this.fetchOrder(this.orderId).catch(this._redirectToList);
    window.scrollTo(0, 0);
  },
};
</script>

<style lang="scss" scoped>
.text-decoration-none {
  text-decoration: none;
}

.lighten {
  color: #9887af;
}

.v-separator {
  border-right: 1px solid #e0e0e0;
}

.h-separator {
  margin-bottom: 12px;
  border-bottom: 1px solid #e0e0e0;
}

.link,
.link:link,
.link:visited {
  color: #673ab7;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}

.note {
  color: #9887af;
  font-size: 11px;
}

.sale {
  text-decoration: line-through;
}

.hover-underline:hover {
  text-decoration: underline !important;
  cursor: pointer;
}

.footer-buttons {
  overflow-x: auto;
  overflow-y: hidden;
}

.clear-changes-btn {
  margin-right: -12px;
  margin-left: -12px;
  width: calc(100% + 24px);
}
</style>
